import { type TouchEvent, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { cn } from '@workspace/ui/lib/utils';

import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import { defaultSrcSetParams } from '~/components/CloudflareResponsiveImage/constants';
import { ChevronRightIcon, CloseIcon } from '~/components/MarketplaceIcons';

interface Props {
  images: string[];
  index: number;
  title: string;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}

const FULL_IMAGE_WIDTH = 1600;
const THUMB_IMAGE_WIDTH = 160;
const SWIPE_THRESHOLD = 40;
const BODY_LOCK_CLASS = 'overflow-hidden!';

const ROUND_BUTTON_CLASS =
  'box-border flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full border border-[#EDEDED] border-solid bg-white p-0 text-[#141415] transition-colors duration-[120ms] hover:border-[#141415] disabled:cursor-default disabled:opacity-40';

export const ProductDELightbox = ({
  images,
  index,
  title,
  onIndexChange,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const thumbsRef = useRef<HTMLDivElement>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const count = images.length;

  const go = (step: number) => onIndexChange((index + step + count) % count);

  useEffect(() => {
    const strip = thumbsRef.current;
    const thumb = strip?.querySelectorAll('button')[index];
    if (strip && thumb) {
      strip.scrollTo({
        left: thumb.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2,
        behavior: 'smooth',
      });
    }
  }, [index]);

  useEffect(() => {
    closeButtonRef.current?.focus();
    document.body.classList.add(BODY_LOCK_CLASS);

    return () => document.body.classList.remove(BODY_LOCK_CLASS);
  }, []);

  useEffect(() => {
    const keepFocusInside = (event: KeyboardEvent) => {
      const buttons = Array.from(
        dialogRef.current?.querySelectorAll<HTMLButtonElement>(
          'button:not([disabled])',
        ) ?? [],
      ).filter((button) => button.offsetParent !== null);
      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      const active = document.activeElement;
      const isInside = Boolean(active && dialogRef.current?.contains(active));

      if (!first || !last) {
        return;
      }
      if (!isInside || (event.shiftKey ? active === first : active === last)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'Tab') {
        keepFocusInside(event);
      } else if (event.key === 'ArrowRight' && count > 1) {
        go(1);
      } else if (event.key === 'ArrowLeft' && count > 1) {
        go(-1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [index, count, onClose, onIndexChange]);

  const handleTouchStart = (event: TouchEvent) => {
    const touch = event.touches[0];
    touchStart.current =
      touch && event.touches.length === 1
        ? { x: touch.clientX, y: touch.clientY }
        : null;
  };

  const handleTouchEnd = (event: TouchEvent) => {
    const start = touchStart.current;
    const touch = event.changedTouches[0];
    touchStart.current = null;
    if (!start || !touch || count < 2) {
      return;
    }
    const dx = touch.clientX - start.x;
    if (
      Math.abs(dx) >= SWIPE_THRESHOLD &&
      Math.abs(dx) > Math.abs(touch.clientY - start.y)
    ) {
      go(dx < 0 ? 1 : -1);
    }
  };

  const image = images[index];

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[1002] flex flex-col bg-white text-[#141415] antialiased [font-family:var(--fontFamily2)]"
    >
      <div className="flex shrink-0 items-center gap-3 desktop:px-10 px-4 desktop:py-5 py-3">
        <span className="min-w-0 flex-1 truncate font-semibold text-[16px] leading-[22px]">
          {title}
        </span>
        <button
          ref={closeButtonRef}
          type="button"
          aria-label={t('close')}
          onClick={onClose}
          className={ROUND_BUTTON_CLASS}
        >
          <CloseIcon className="size-5" />
        </button>
      </div>
      <div
        className="relative flex min-h-0 flex-1 items-center justify-center desktop:px-24 px-4"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {image && (
          <CloudflareResponseImage
            key={image}
            src={image}
            srcParams={{ width: FULL_IMAGE_WIDTH }}
            srcSetParams={defaultSrcSetParams}
            sizes="100vw"
            alt={title}
            draggable={false}
            className="max-h-full max-w-full select-none object-contain"
          />
        )}
        {count > 1 && (
          <>
            <button
              type="button"
              aria-label={t('productDE:photoPrev')}
              onClick={() => go(-1)}
              className={cn(
                ROUND_BUTTON_CLASS,
                'absolute top-1/2 desktop:left-10 left-4 desktop:flex hidden -translate-y-1/2',
              )}
            >
              <ChevronRightIcon className="size-5 rotate-180" />
            </button>
            <button
              type="button"
              aria-label={t('productDE:photoNext')}
              onClick={() => go(1)}
              className={cn(
                ROUND_BUTTON_CLASS,
                'absolute top-1/2 desktop:right-10 right-4 desktop:flex hidden -translate-y-1/2',
              )}
            >
              <ChevronRightIcon className="size-5" />
            </button>
          </>
        )}
      </div>
      {count > 1 && (
        <>
          <div className="flex desktop:hidden h-13 shrink-0 items-center justify-center gap-1.5">
            {images.map((dot, dotIndex) => (
              <button
                key={dot}
                type="button"
                aria-label={`${dotIndex + 1}`}
                aria-pressed={dotIndex === index}
                onClick={() => onIndexChange(dotIndex)}
                className={cn(
                  'size-1.5 shrink-0 cursor-pointer rounded-full border-0 p-0 transition-colors duration-[120ms]',
                  dotIndex === index ? 'bg-[#141415]' : 'bg-[#D9D9D9]',
                )}
              />
            ))}
          </div>
          <div
            ref={thumbsRef}
            className="scrollbar-hide relative desktop:block hidden shrink-0 overflow-x-auto px-10 pt-3 pb-6"
          >
            <div className="mx-auto flex w-max gap-2">
              {images.map((thumb, thumbIndex) => (
                <button
                  key={thumb}
                  type="button"
                  aria-label={`${thumbIndex + 1}`}
                  aria-pressed={thumbIndex === index}
                  onClick={() => onIndexChange(thumbIndex)}
                  className={cn(
                    'box-border size-20 shrink-0 cursor-pointer rounded-[8px] border border-[#EDEDED] border-solid bg-white p-1.5 transition-colors duration-[120ms] hover:border-[#141415]',
                    thumbIndex === index &&
                      'border-2 border-[#E94E1A] hover:border-[#E94E1A]',
                  )}
                >
                  <CloudflareResponseImage
                    src={thumb}
                    srcParams={{ width: THUMB_IMAGE_WIDTH }}
                    srcSetParams={defaultSrcSetParams}
                    sizes="80px"
                    alt=""
                    draggable={false}
                    className="size-full object-contain"
                  />
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>,
    document.body,
  );
};
